import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProfile, updateProfile, changePassword, getUserStats } from '../services/api';
import Navbar from '../components/Navbar';
import { useTheme } from '../context/ThemeContext';
import useWindowSize from '../hooks/useWindowSize';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EventIcon from '@mui/icons-material/Event';

function Profile() {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [editMode, setEditMode] = useState(false);
  const [passwordMode, setPasswordMode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/'); return; }
    getProfile()
      .then(res => {
        setUser(res.data);
        setEditForm({ name: res.data.name, email: res.data.email });
      })
      .catch(() => navigate('/'));
    getUserStats()
      .then(res => setStats(res.data))
      .catch(() => {});
  }, [navigate]);

  const handleUpdateProfile = async () => {
    setError('');
    setSuccess('');
    if (!editForm.name.trim() || !editForm.email.trim()) return setError('Rellena todos los campos');
    setLoading(true);
    try {
      const res = await updateProfile(editForm);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      setUser(res.data.user);
      setEditMode(false);
      setSuccess('Perfil actualizado correctamente');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar el perfil');
    }
    setLoading(false);
  };

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) return setError('Rellena todos los campos');
    if (passwordForm.newPassword !== passwordForm.confirmPassword) return setError('Las contraseñas no coinciden');
    if (passwordForm.newPassword.length < 6) return setError('La nueva contraseña debe tener al menos 6 caracteres');
    setLoading(true);
    try {
      await changePassword({ currentPassword: passwordForm.currentPassword, newPassword: passwordForm.newPassword });
      setPasswordMode(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSuccess('Contraseña actualizada correctamente');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cambiar la contraseña');
    }
    setLoading(false);
  };

  if (!user) return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />
      <p style={{ color: colors.textSecondary, padding: '2rem' }}>Cargando...</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />
      <div style={{ padding: isMobile ? '1rem' : '2rem', maxWidth: '700px', margin: '0 auto' }}>

        <h1 style={{ color: colors.text, fontSize: isMobile ? '1.4rem' : '2rem', fontWeight: 'bold', marginBottom: isMobile ? '1rem' : '2rem' }}>
          Mi perfil
        </h1>

        {error && <div style={{ backgroundColor: '#ff444422', border: '1px solid #ff4444', color: '#ff4444', padding: '0.8rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</div>}
        {success && <div style={{ backgroundColor: '#00cc6622', border: '1px solid #00cc66', color: '#00cc66', padding: '0.8rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' }}>{success}</div>}

        {/* ESTADÍSTICAS */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? '0.6rem' : '1rem', marginBottom: '1.5rem' }}>
            <div style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', border: `1px solid ${colors.border}`, padding: isMobile ? '1rem' : '1.5rem', display: 'flex', alignItems: 'center', gap: isMobile ? '0.6rem' : '1rem' }}>
              <div style={{ backgroundColor: colors.bgTertiary, borderRadius: '12px', padding: isMobile ? '0.6rem' : '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ConfirmationNumberIcon style={{ color: colors.accent, fontSize: isMobile ? 20 : 28 }} />
              </div>
              <div>
                <p style={{ color: colors.textSecondary, fontSize: isMobile ? '0.7rem' : '0.8rem', marginBottom: '0.2rem' }}>Entradas compradas</p>
                <p style={{ color: colors.text, fontSize: isMobile ? '1.4rem' : '1.8rem', fontWeight: 'bold' }}>{stats.total_tickets}</p>
              </div>
            </div>
            <div style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', border: `1px solid ${colors.border}`, padding: isMobile ? '1rem' : '1.5rem', display: 'flex', alignItems: 'center', gap: isMobile ? '0.6rem' : '1rem' }}>
              <div style={{ backgroundColor: colors.bgTertiary, borderRadius: '12px', padding: isMobile ? '0.6rem' : '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <EventIcon style={{ color: colors.accent, fontSize: isMobile ? 20 : 28 }} />
              </div>
              <div>
                <p style={{ color: colors.textSecondary, fontSize: isMobile ? '0.7rem' : '0.8rem', marginBottom: '0.2rem' }}>Eventos asistidos</p>
                <p style={{ color: colors.text, fontSize: isMobile ? '1.4rem' : '1.8rem', fontWeight: 'bold' }}>{stats.total_events}</p>
              </div>
            </div>
          </div>
        )}

        {/* DATOS PERSONALES */}
        <div style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', border: `1px solid ${colors.border}`, padding: isMobile ? '1rem' : '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
              <AccountCircleIcon style={{ color: colors.accent, fontSize: isMobile ? 24 : 32 }} />
              <h2 style={{ color: colors.text, fontWeight: 'bold', fontSize: isMobile ? '1rem' : '1.1rem' }}>Datos personales</h2>
            </div>
            {!editMode ? (
              <button style={{ backgroundColor: 'transparent', border: `1px solid ${colors.border}`, color: colors.textSecondary, borderRadius: '20px', padding: '0.4rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }} onClick={() => { setEditMode(true); setPasswordMode(false); setError(''); setSuccess(''); }}>
                <EditIcon style={{ fontSize: 16 }} /> Editar
              </button>
            ) : (
              <button style={{ backgroundColor: 'transparent', border: `1px solid ${colors.border}`, color: colors.textSecondary, borderRadius: '20px', padding: '0.4rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }} onClick={() => { setEditMode(false); setError(''); }}>
                <CloseIcon style={{ fontSize: 16 }} /> Cancelar
              </button>
            )}
          </div>

          {!editMode ? (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
              <div>
                <p style={{ color: colors.textSecondary, fontSize: '0.8rem', marginBottom: '0.3rem' }}>Nombre</p>
                <p style={{ color: colors.text, fontWeight: '500' }}>{user.name}</p>
              </div>
              <div>
                <p style={{ color: colors.textSecondary, fontSize: '0.8rem', marginBottom: '0.3rem' }}>Email</p>
                <p style={{ color: colors.text, fontWeight: '500', wordBreak: 'break-all' }}>{user.email}</p>
              </div>
              <div>
                <p style={{ color: colors.textSecondary, fontSize: '0.8rem', marginBottom: '0.3rem' }}>Miembro desde</p>
                <p style={{ color: colors.text, fontWeight: '500' }}>{new Date(user.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              </div>
              {stats?.last_ticket && (
                <div>
                  <p style={{ color: colors.textSecondary, fontSize: '0.8rem', marginBottom: '0.3rem' }}>Última compra</p>
                  <p style={{ color: colors.text, fontWeight: '500' }}>{stats.last_ticket.event_name}</p>
                  <p style={{ color: colors.textSecondary, fontSize: '0.8rem' }}>{new Date(stats.last_ticket.created_at).toLocaleDateString('es-ES')}</p>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ color: colors.textSecondary, fontSize: '0.85rem', display: 'block', marginBottom: '0.4rem' }}>Nombre completo</label>
                <input style={{ width: '100%', backgroundColor: colors.inputBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '0.7rem 1rem', color: colors.text, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} value={editForm.name} onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label style={{ color: colors.textSecondary, fontSize: '0.85rem', display: 'block', marginBottom: '0.4rem' }}>Email</label>
                <input style={{ width: '100%', backgroundColor: colors.inputBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '0.7rem 1rem', color: colors.text, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} type="email" value={editForm.email} onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <button style={{ backgroundColor: colors.accent, color: colors.accentText, border: 'none', padding: '0.8rem', borderRadius: '25px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }} onClick={handleUpdateProfile} disabled={loading}>
                <SaveIcon style={{ fontSize: 18 }} />
                {loading ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          )}
        </div>

        {/* CAMBIAR CONTRASEÑA */}
        <div style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', border: `1px solid ${colors.border}`, padding: isMobile ? '1rem' : '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: passwordMode ? '1.5rem' : '0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
              <LockIcon style={{ color: colors.accent, fontSize: isMobile ? 24 : 28 }} />
              <h2 style={{ color: colors.text, fontWeight: 'bold', fontSize: isMobile ? '1rem' : '1.1rem' }}>Contraseña</h2>
            </div>
            {!passwordMode ? (
              <button style={{ backgroundColor: 'transparent', border: `1px solid ${colors.border}`, color: colors.textSecondary, borderRadius: '20px', padding: '0.4rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }} onClick={() => { setPasswordMode(true); setEditMode(false); setError(''); setSuccess(''); }}>
                <EditIcon style={{ fontSize: 16 }} /> Cambiar
              </button>
            ) : (
              <button style={{ backgroundColor: 'transparent', border: `1px solid ${colors.border}`, color: colors.textSecondary, borderRadius: '20px', padding: '0.4rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }} onClick={() => { setPasswordMode(false); setError(''); }}>
                <CloseIcon style={{ fontSize: 16 }} /> Cancelar
              </button>
            )}
          </div>

          {passwordMode && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {[
                { label: 'Contraseña actual', key: 'currentPassword' },
                { label: 'Nueva contraseña', key: 'newPassword' },
                { label: 'Confirmar nueva contraseña', key: 'confirmPassword' },
              ].map(field => (
                <div key={field.key}>
                  <label style={{ color: colors.textSecondary, fontSize: '0.85rem', display: 'block', marginBottom: '0.4rem' }}>{field.label}</label>
                  <input style={{ width: '100%', backgroundColor: colors.inputBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '0.7rem 1rem', color: colors.text, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }} type="password" placeholder="••••••••" value={passwordForm[field.key]} onChange={(e) => setPasswordForm(f => ({ ...f, [field.key]: e.target.value }))} />
                </div>
              ))}
              <button style={{ backgroundColor: colors.accent, color: colors.accentText, border: 'none', padding: '0.8rem', borderRadius: '25px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.95rem' }} onClick={handleChangePassword} disabled={loading}>
                {loading ? 'Guardando...' : 'Cambiar contraseña'}
              </button>
            </div>
          )}
        </div>

        {/* ACCESOS RÁPIDOS */}
        <div style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', border: `1px solid ${colors.border}`, padding: isMobile ? '1rem' : '1.5rem' }}>
          <h2 style={{ color: colors.text, fontWeight: 'bold', fontSize: isMobile ? '1rem' : '1.1rem', marginBottom: '1rem' }}>Accesos rápidos</h2>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem' }}>
            <button style={{ flex: 1, backgroundColor: 'transparent', border: `1px solid ${colors.border}`, color: colors.text, padding: '0.8rem', borderRadius: '12px', cursor: 'pointer', fontSize: '0.9rem' }} onClick={() => navigate('/my-tickets')}>
              🎟️ Mis entradas
            </button>
            {user.role === 'admin' && (
              <button style={{ flex: 1, backgroundColor: 'transparent', border: `1px solid ${colors.accent}`, color: colors.accent, padding: '0.8rem', borderRadius: '12px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 'bold' }} onClick={() => navigate('/admin')}>
                ⚙️ Panel admin
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default Profile;